import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from 'src/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-[6px] whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-white hover:bg-brand-deep hover:shadow-[0_2px_6px_rgba(32,104,178,0.25)]',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-card text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-brand-bg-subtle hover:text-primary',
        link: 'text-primary underline-offset-4 hover:underline',
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        addField:
          'bg-[#F1F5F9] dark:bg-card text-gray-600 dark:text-muted-foreground hover:bg-[#E2E8F0] dark:hover:bg-border shadow-sm',
        ghostV2:
          'inline-flex items-center justify-center gap-[6px] whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 px-4 py-2 text-white/95 hover:text-white hover:bg-white/15 backdrop-blur-[1px] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] transition-transform duration-150 active:scale-[0.98] motion-reduce:transition-none motion-reduce:transform-none',
        outlineV2:
          'inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-background text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer',
        // Deliberately carries NO bg/text/hover color of its own — just the
        // bordered shape. Exists for callers that supply their own
        // rest+hover colors per className (e.g. GenericFlowAction.jsx's
        // per-action tone classes, src/lib/statusStyles.js's
        // TONE_BUTTON_CLASSES). `buttonVariants({variant, size, className})`
        // merges `className` in via cva's own internal clsx — NOT
        // tailwind-merge — so a caller's className can't reliably override
        // a variant's own color classes (confirmed live: layering tone
        // classes on top of `outline`'s hover:text-primary left hover text
        // blue regardless of the caller's own hover:text-* class, since
        // clsx doesn't dedupe/override, cascade order does, and the
        // variant's own class was winning). Any variant a caller fully
        // recolors itself should start from `tone`, not `outline`/
        // `outlineV2`, to avoid this exact class of bug.
        tone: 'border bg-transparent',
      },
      size: {
        default: 'h-8.5 px-5 py-[7px]',
        sm: 'h-7 rounded-sm px-3',
        headerSm: 'h-auto px-3.5 py-[7px] rounded-[7px] text-[12px] font-[550]',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
